/**
 * Inline Annotations Styles
 *
 * StyleSheet definitions for the inline annotations module.
 * Design System: Y2K + Glassmorphism
 */

import { StyleSheet, Platform, ViewStyle } from 'react-native';
import { SEVERITY_COLORS } from './types';
import { createBoxShadow } from '../../../lib/design-tokens';

// Platform-aware shadow styles for web/native compatibility
const segmentButtonShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 1,
  blurRadius: 3,
  opacity: 0.1,
  elevation: 2,
});

const popoverShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 8,
  blurRadius: 24,
  opacity: 0.2,
  elevation: 16,
});

const bottomSheetShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: -4,
  blurRadius: 16,
  opacity: 0.3,
  elevation: 24,
});

export const styles = StyleSheet.create({
  // Message display container
  messageContainer: {
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(212, 165, 255, 0.2)',
    // NOTE: Do NOT use overflow: 'hidden' - it truncates long messages
  },

  // Parent text element for inline flow (wraps all segments)
  messageText: {
    fontSize: 16,
    lineHeight: 26,
    color: '#1F2937',
    fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
    ...(Platform.OS === 'web' && {
      // Web-specific: ensure proper word breaking
      wordBreak: 'break-word' as const,
      overflowWrap: 'break-word' as const,
    }),
  },

  // Text line for wrapping (kept for backwards compatibility)
  textLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'baseline', // Better alignment for inline text
    ...(Platform.OS === 'web' && {
      // Web-specific: ensure proper word breaking
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    }),
  },

  // Normal text segment
  normalText: {
    fontSize: 16,
    lineHeight: 26,
    color: '#1F2937',
    fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
  },

  // Issue highlight base (block-level - kept for backwards compatibility)
  issueHighlight: {
    borderRadius: 4,
    paddingHorizontal: 2,
    marginHorizontal: 1,
  },

  // Issue highlight inline (for nested Text flow)
  issueHighlightInline: {
    borderRadius: 4,
    paddingHorizontal: 2,
  },

  // Issue highlight active state
  issueHighlightActive: {
    opacity: 0.8,
  },

  // Issue text (block-level - kept for backwards compatibility)
  issueText: {
    fontSize: 16,
    lineHeight: 26,
    fontWeight: '500',
    fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
  },

  // Issue text inline (for nested Text flow)
  issueTextInline: {
    fontWeight: '500',
    // Inherits fontSize/lineHeight from parent messageText
  },

  // Footnote superscript badge (block-level - kept for backwards compatibility)
  footnoteBadge: {
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 2,
  },

  // Footnote superscript (inline Text version)
  footnoteSuperscript: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 1,
    marginLeft: 2,
    ...(Platform.OS === 'web' && {
      // Web: Position as superscript
      verticalAlign: 'super' as const,
      lineHeight: 1,
    }),
  },

  footnoteText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  // Accepted issue strikethrough
  acceptedText: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },

  // Replacement text (green)
  replacementText: {
    fontSize: 16,
    lineHeight: 26,
    color: '#059669',
    fontWeight: '500',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 2,
  },

  // Revised view - green highlight for accepted replacements
  revisedReplacementHighlight: {
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    borderRadius: 4,
    paddingHorizontal: 4,
  },

  // Revised view - replacement text styling
  revisedReplacementText: {
    color: '#047857',
    fontWeight: '500',
    fontFamily: Platform.OS === 'web' ? 'Inter, system-ui, sans-serif' : 'System',
  },

  // Ignored issue dimmed
  ignoredHighlight: {
    opacity: 0.4,
  },

  // Segmented control
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 10,
    padding: 4,
    marginBottom: 16,
    alignSelf: 'center',
    maxWidth: 300,
  },

  segmentButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },

  segmentButtonActive: {
    backgroundColor: '#FFFFFF',
    ...segmentButtonShadow,
  } as ViewStyle,

  segmentText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },

  segmentTextActive: {
    color: '#1F2937',
    fontWeight: '600',
  },

  // Change indicator dot
  changeIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#10B981',
    marginLeft: 6,
  },

  // Progress indicator
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },

  progressBar: {
    flex: 1,
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },

  progressFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 2,
  },

  progressText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },

  // Copy revised bar
  copyBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    borderRadius: 8,
    marginTop: 16,
  },

  copyBarText: {
    fontSize: 14,
    color: '#059669',
    fontWeight: '500',
  },

  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#10B981',
    borderRadius: 6,
  },

  copyButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },

  copyButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },

  // Popover styles (web)
  popoverOverlay: {
    position: 'fixed' as any, // Use fixed positioning for viewport-relative overlay
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 100000, // Very high to ensure above tab bar
  },

  popoverContainer: {
    position: 'fixed' as any, // Use fixed positioning for correct placement
    width: 320,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    zIndex: 100001, // Very high to ensure above tab bar and overlay
    overflow: 'hidden',
    ...popoverShadow,
  } as unknown as ViewStyle,

  // Bottom sheet container (slides up from bottom)
  popoverBottomSheet: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%' as any,
    maxWidth: 480,
    maxHeight: '90dvh' as any, // Dynamic viewport height (accounts for mobile browser chrome)
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    overflow: 'hidden' as const,
    marginLeft: 'auto' as any,
    marginRight: 'auto' as any,
    ...bottomSheetShadow,
    // Web-specific: CSS transition and initial transform for smooth animation
    ...(Platform.OS === 'web' && {
      transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)' as any,
      transform: 'translateY(100%)' as any, // Start hidden (off-screen)
    }),
  } as unknown as ViewStyle,

  // Hidden state (off-screen at bottom) - web only
  popoverBottomSheetHidden: {
    ...(Platform.OS === 'web' && {
      transform: 'translateY(100%)' as any,
    }),
  },

  // Visible state (slide into view) - web only
  popoverBottomSheetVisible: {
    ...(Platform.OS === 'web' && {
      transform: 'translateY(0)' as any,
    }),
  },

  // ScrollView for modal content
  popoverScrollView: {
    flex: 1,
  },

  popoverArrow: {
    position: 'absolute',
    width: 16,
    height: 16,
    backgroundColor: '#FFFFFF',
    transform: [{ rotate: '45deg' }],
    zIndex: 100,
  },

  popoverContent: {
    padding: 16,
  },

  popoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },

  popoverSeverityBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },

  popoverSeverityText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },

  popoverTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
  },

  popoverCloseButton: {
    padding: 4,
  },

  popoverSnippet: {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },

  popoverSnippetText: {
    fontSize: 14,
    color: '#374151',
    fontStyle: 'italic',
  },

  popoverDescription: {
    marginBottom: 14,
  },

  popoverDescriptionText: {
    fontSize: 13,
    lineHeight: 20,
    color: '#374151', // Secondary text color (not green)
  },

  popoverDramaReduction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
  },

  dramaReductionBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: 'rgba(16, 185, 129, 0.15)',
    borderRadius: 12,
  },

  dramaReductionText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#059669',
  },

  // Explanation accordion
  explanationContainer: {
    marginBottom: 16,
  },

  explanationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },

  explanationTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },

  explanationList: {
    gap: 8,
    paddingTop: 8,
  },

  explanationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },

  explanationBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#9CA3AF',
    marginTop: 8,
  },

  explanationText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 20,
    color: '#6B7280',
  },

  // Action buttons
  actionButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },

  acceptButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    backgroundColor: '#10B981',
    borderRadius: 8,
  },

  acceptButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },

  ignoreButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 8,
  },

  ignoreButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },

  // Bottom sheet styles (native)
  bottomSheetBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },

  bottomSheetContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },

  bottomSheetHandle: {
    width: 40,
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 12,
    marginBottom: 8,
  },

  bottomSheetNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.08)',
  },

  bottomSheetNavButton: {
    padding: 8,
  },

  bottomSheetNavText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },

  bottomSheetNavDisabled: {
    opacity: 0.3,
  },

  bottomSheetNavArrow: {
    fontSize: 18,
    color: '#1F2937',
  },

  bottomSheetNavArrowDisabled: {
    fontSize: 18,
    color: '#D1D5DB',
  },

  bottomSheetContent: {
    padding: 20,
  },

  // Rewrite "Try instead" section
  rewriteSection: {
    marginVertical: 12,
  },

  rewriteLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },

  rewriteLabelText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#059669',
  },

  rewriteContent: {
    padding: 12,
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.2)',
    borderRadius: 8,
  },

  rewriteText: {
    fontSize: 14,
    lineHeight: 21,
    color: '#1F2937',
  },

  // Two-section layout
  issueAnalysisSection: {
    backgroundColor: 'rgba(254, 243, 199, 0.5)', // Yellow tint - educational
    padding: 16,
  },

  suggestionSection: {
    backgroundColor: 'rgba(239, 246, 255, 0.5)', // Blue tint - actionable
    padding: 16,
  },

  sectionDivider: {
    height: 2,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },

  // No suggestion banner
  noSuggestionBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.08)',
  },

  noSuggestionIcon: {
    fontSize: 20,
  },

  noSuggestionContent: {
    flex: 1,
    gap: 4,
  },

  noSuggestionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },

  noSuggestionText: {
    fontSize: 13,
    lineHeight: 18,
    color: '#6B7280',
  },
});

// Header styles
export const headerStyles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  issueBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: 'rgba(217, 119, 6, 0.15)',
    borderRadius: 12,
  },
  issueBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#D97706',
  },
});

// Score Delta Indicator styles (Phase 2 Gamification)
export const scoreDeltaStyles = StyleSheet.create({
  container: {
    marginTop: 12,
    marginBottom: 8,
    alignItems: 'flex-start',
  },
});

/**
 * Get severity-specific styles for highlights
 */
export function getSeverityStyles(severity: keyof typeof SEVERITY_COLORS) {
  const colors = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  return {
    highlight: {
      backgroundColor: colors.bg,
    },
    text: {
      color: colors.text,
    },
    badge: {
      backgroundColor: colors.border,
    },
  };
}

export default styles;
